import {
  TextField,
  BulkExportButton,
  BulkDeleteButton,
  BooleanField,
  TopToolbar,
  CreateButton,
  DateField,
  ExportButton,
  NumberField,
  ReferenceField,
  FunctionField,
  ListContextProvider,
  useListController,
  SelectColumnsButton,
} from "react-admin";
import { ChunkedExportButton } from "@/components/common/ChunkedExportButton";
import { Download as DownloadIcon } from "@mui/icons-material";
import { getProps } from "@/utils/helpers/columnsHelper";
import { StatusChipField } from "@/components/common/fields/StatusChipField";
import { StoplossClaimListAside } from "./components/StoplossClaimListAside";
import { ResourceName } from "@/types";
import type { StoplossClaim } from "@/types/stoplossClaim";
import List from "@/components/ui/react-admin/styledList/StyledList";
import { StyledDatagridConfigurable } from "@/components/ui/react-admin/StyledDatagrid";
import { PageHeader } from "@/components/common/PageHeader";
import { getResourceIcon } from "@/utils/icons";
import { TruncatedFieldWrapper } from "@/components/common/fields/TruncatedFieldWrapper";
import { jsonExporter } from "@/utils/exporters/jsonExporter";
import { defaultCurrencyFormatterOptions } from "@/utils/formatters/currencyFormatter";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { ListEmptyState } from "@/components/common/ListEmptyState";
import { isNonProductionEnvironment } from "@/utils/environment";

// Custom bulk action buttons
const StoplossClaimsBulkActionButtons = () => {
  const { canDelete } = useResourcePermissions(ResourceName.STOPLOSS_CLAIMS);

  return (
    <>
      <BulkExportButton />
      {isNonProductionEnvironment() && (
        <BulkExportButton
          label="Export as JSON"
          icon={<DownloadIcon />}
          exporter={(records) => jsonExporter(records, ResourceName.STOPLOSS_CLAIMS)}
        />
      )}
      {canDelete && <BulkDeleteButton />}
    </>
  );
};

// Top toolbar actions
const StoplossClaimsListActions = ({ loading }: { loading?: boolean }) => {
  const { canCreate } = useResourcePermissions(ResourceName.STOPLOSS_CLAIMS);

  return (
    <TopToolbar>
      {canCreate && <CreateButton disabled={loading} />}
      <SelectColumnsButton disabled={loading} />
      <ChunkedExportButton disabled={loading} />
      {isNonProductionEnvironment() && (
        <ExportButton
          label="Export JSON"
          icon={<DownloadIcon />}
          exporter={(records) => jsonExporter(records, ResourceName.STOPLOSS_CLAIMS)}
          disabled={loading}
        />
      )}
    </TopToolbar>
  );
};

export const StoplossClaimsList = () => {
  // Explicit default sort preserves the prior server order (newest submitted
  // first) now that the API honors sort. Without it react-admin would default
  // to id ASC.
  const listContext = useListController({
    resource: ResourceName.STOPLOSS_CLAIMS,
    sort: { field: "submitted_date", order: "DESC" },
  });

  const { isPending: loading } = listContext;

  const StoplossClaimIcon = getResourceIcon(ResourceName.STOPLOSS_CLAIMS);

  return (
    <ListContextProvider value={listContext}>
      <PageHeader
        customTitle="Stop-Loss Claims"
        hideSectionTitle
        rightContent={<StoplossClaimsListActions loading={loading} />}
        headerIcon={<StoplossClaimIcon sx={{ fontSize: 28, color: "primary.main" }} />}
      />
      <List
        perPage={50}
        sort={{ field: "submitted_date", order: "DESC" }}
        actions={false}
        aside={<StoplossClaimListAside />}
        loading={loading}
        empty={<ListEmptyState resource={ResourceName.STOPLOSS_CLAIMS} />}
      >
        <StyledDatagridConfigurable
          bulkActionButtons={<StoplossClaimsBulkActionButtons />}
          omit={[
            "aegle_id",
            "stoploss_claim_type",
            "submitted_date",
            "last_paid_date",
            "first_service_date",
            "last_service_date",
            "claim_items_count",
            "total_billed_amount",
            "total_allowed_amount",
          ]}
        >
          {/* #7244: a claim held by a merged-and-retired record links to its LIVE
              survivor -- the retired row is deleted, so a claimant_id_ref
              reference would render blank. */}
          <FunctionField
            {...getProps("claimant_id_ref", ResourceName.STOPLOSS_CLAIMS)}
            sortable={false}
            render={(claim: StoplossClaim) => (
              <ReferenceField
                record={claim}
                source={claim.merged_into_claimant_id_ref ? "merged_into_claimant_id_ref" : "claimant_id_ref"}
                reference={ResourceName.CLAIMANTS}
              >
                <TextField source="claimant_name" />
              </ReferenceField>
            )}
          />
          <ReferenceField
            {...getProps("client_id_ref", ResourceName.STOPLOSS_CLAIMS)}
            reference={ResourceName.CLIENTS}
            sortable={false}
          >
            <TextField source="client_legal_name" />
          </ReferenceField>
          <NumberField
            {...getProps("total_paid_amount", ResourceName.STOPLOSS_CLAIMS)}
            options={defaultCurrencyFormatterOptions}
          />
          <StatusChipField {...getProps("claim_status", ResourceName.STOPLOSS_CLAIMS)} />
          {/* First-class Aura submission state (US 5331) — replaces the funded-amount proxy.
              Header is "Aura" (not "Submitted to Aura") so it doesn't collide with the
              carrier "Submitted" claim_status shown in the Status column; the check's
              tooltip carries the full meaning. */}
          <BooleanField
            source="submitted_to_aura"
            label="Aura"
            valueLabelTrue="Submitted to Aura"
            valueLabelFalse="Not submitted to Aura"
            sortable={false}
            FalseIcon={null}
          />
          <DateField {...getProps("created_datetime")} showTime={false} />
          {/* Hidden by default — user can toggle via SelectColumnsButton */}
          <TruncatedFieldWrapper {...getProps("aegle_id", ResourceName.STOPLOSS_CLAIMS)}>
            <TextField {...getProps("aegle_id", ResourceName.STOPLOSS_CLAIMS)} />
          </TruncatedFieldWrapper>
          <TextField {...getProps("stoploss_claim_type", ResourceName.STOPLOSS_CLAIMS)} />
          <DateField {...getProps("submitted_date", ResourceName.STOPLOSS_CLAIMS)} showTime={false} />
          <DateField {...getProps("last_paid_date", ResourceName.STOPLOSS_CLAIMS)} showTime={false} />
          <DateField {...getProps("first_service_date", ResourceName.STOPLOSS_CLAIMS)} showTime={false} />
          <DateField {...getProps("last_service_date", ResourceName.STOPLOSS_CLAIMS)} showTime={false} />
          <NumberField {...getProps("claim_items_count", ResourceName.STOPLOSS_CLAIMS)} sortable={false} />
          <NumberField
            {...getProps("total_billed_amount", ResourceName.STOPLOSS_CLAIMS)}
            options={defaultCurrencyFormatterOptions}
          />
          <NumberField
            {...getProps("total_allowed_amount", ResourceName.STOPLOSS_CLAIMS)}
            options={defaultCurrencyFormatterOptions}
          />
        </StyledDatagridConfigurable>
      </List>
    </ListContextProvider>
  );
};
